// ── PolyAccountStore — Zustand store for Polymarket account state ──
//
// Mirrors accountStore pattern. Separate from marketStore (Hydra pipeline).
// Reuses V1 client.ts functions for auth, trading, and localStorage.

import { create } from 'zustand';
import type { ClobClient } from '@polymarket/clob-client-v2';
import {
  loadPolyUserConfig,
  savePolyUserConfig,
  clearPolyUserConfig,
  createOrDerivePolyApiCreds,
  createPolyTradingClient,
  deriveSignerAddress,
  fetchPolyFunderBalance,
  normalizePrivateKey,
  validatePolyConnectArgs,
  type PolyFunderBalance,
  type PolySignatureType,
  type PolyUserConfig,
} from '../lib/polymarket/client';
import type { PolyOpenOrder } from '../lib/polymarket/types';

// ── Module-level state (not in Zustand — mutable refs) ──────────

let _polyClient: ClobClient | null = null;

// ── Store ───────────────────────────────────────────────────────

export type PolyAccountStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

interface PolyAccountState {
  config: PolyUserConfig | null;
  status: PolyAccountStatus;
  error: string | null;
  /** USDC balance/allowance for the configured funder, fetched at connect time. */
  funderBalance: PolyFunderBalance | null;
  openOrders: PolyOpenOrder[];

  connect: (args: { privateKey: string; signatureType: PolySignatureType; funder?: string }) => Promise<void>;
  disconnect: () => void;
  removeCredentials: () => void;
  refreshOpenOrders: () => Promise<void>;
  getClient: () => ClobClient | null;
}

export const usePolyAccountStore = create<PolyAccountState>((set, get) => ({
  config: null,
  status: 'disconnected',
  error: null,
  funderBalance: null,
  openOrders: [],

  // ── Connect ─────────────────────────────────────────────────
  connect: async (args) => {
    if (get().status === 'connecting') return;
    set({ status: 'connecting', error: null });

    try {
      // 0. Sync pre-flight: format checks + funder required-ness
      const preErr = validatePolyConnectArgs(args);
      if (preErr) throw new Error(preErr);

      const privateKey = normalizePrivateKey(args.privateKey);

      // 1. Derive API credentials + signer address in parallel
      //    (both lazy-load ethers + clob-client-v2; sharing the cached deps)
      const [apiCreds, signerAddress] = await Promise.all([
        createOrDerivePolyApiCreds(privateKey),
        deriveSignerAddress(privateKey),
      ]);

      // 2. Post-derivation: catch signer==funder (the classic Polymarket mistake)
      const postErr = validatePolyConnectArgs(args, signerAddress);
      if (postErr) throw new Error(postErr);

      // 3. Build config (drop funder for EOA — it's ignored anyway)
      const funder =
        args.signatureType === 0 ? undefined : args.funder?.trim() || undefined;
      const config: PolyUserConfig = {
        privateKey,
        signatureType: args.signatureType,
        funder,
        apiCreds,
        signerAddress,
      };

      // 4. Create trading client
      _polyClient = await createPolyTradingClient(config);

      // 5. Verify L2 reachability by fetching collateral balance. In the v5
      //    SDK this does not send the constructor funder address, so it is an
      //    auth/signature-type sanity check rather than proof that the typed
      //    funder matches the signer. We still refuse "connected" on failure.
      let funderBalance: PolyFunderBalance;
      try {
        funderBalance = await fetchPolyFunderBalance(_polyClient);
      } catch (err) {
        _polyClient = null;
        const detail = err instanceof Error ? err.message : String(err);
        throw new Error(
          `Polymarket balance lookup failed. Check the private key/API credentials and signature type, then verify the funder address on your Polymarket profile. (${detail})`
        );
      }

      savePolyUserConfig(config);
      set({ status: 'connected', error: null, config, funderBalance });
    } catch (err) {
      _polyClient = null;
      const message = err instanceof Error ? err.message : 'Polymarket connection failed';
      console.error('[PolyAccountStore] connect failed:', message);
      set({ status: 'error', error: message, funderBalance: null, openOrders: [] });
    }
  },

  // ── Disconnect ──────────────────────────────────────────────
  disconnect: () => {
    _polyClient = null;
    set({
      status: 'disconnected',
      error: null,
      funderBalance: null,
      openOrders: [],
    });
  },

  // ── Remove Credentials (disconnect + clear storage) ─────────
  removeCredentials: () => {
    get().disconnect();
    clearPolyUserConfig();
    set({ config: null });
  },

  // ── Refresh Open Orders ─────────────────────────────────────
  refreshOpenOrders: async () => {
    if (!_polyClient) return;
    try {
      const maybe = _polyClient as unknown as {
        getOpenOrders?: () => Promise<unknown>;
      };
      const raw = await maybe.getOpenOrders?.();
      if (!Array.isArray(raw)) return;

      const n = (v: unknown) => {
        const x = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
        return Number.isFinite(x) ? x : 0;
      };

      set({
        openOrders: raw.map((o) => {
          const rec: Record<string, unknown> =
            o && typeof o === 'object' ? (o as Record<string, unknown>) : {};
          return {
            id: String(rec.id ?? rec.orderID ?? rec.orderId ?? ''),
            assetId: String(rec.asset_id ?? rec.assetId ?? ''),
            conditionId: String(rec.market ?? rec.conditionId ?? ''),
            side: String(rec.side ?? ''),
            price: n(rec.price),
            originalSize: n(rec.original_size ?? rec.size ?? rec.originalSize),
            sizeMatched: n(rec.size_matched ?? rec.sizeMatched),
            timestampMs: rec.timestamp ? n(rec.timestamp) * 1000 : undefined,
          };
        }),
      });
    } catch (err) {
      console.warn('[PolyAccountStore] refreshOpenOrders failed:', err);
    }
  },

  // ── Get Client (for trading operations) ─────────────────────
  getClient: () => _polyClient,
}));

// ── Auto-reconnect helper (used by usePolyAccount hook) ────────

export async function autoConnectPoly(): Promise<void> {
  const saved = loadPolyUserConfig();
  if (!saved?.apiCreds || !saved.privateKey) return;

  await usePolyAccountStore.getState().connect({
    privateKey: saved.privateKey,
    signatureType: saved.signatureType,
    funder: saved.funder,
  });
}
